import { useEffect, useMemo, useState } from 'react'
import { authFetch } from '@/auth/authFetch'
import { API_BASE_URL } from '@/analysis/endpoints'

type ReportRow = {
  key: string
  label: string
  critical: number
  warning: number
  ok: number
  total: number
}

type StreamNode = {
  studio?: string | null
  game?: string | null
  avgSeverity?: number | null
}

function accumulate(map: Map<string, ReportRow>, key: string, sev: number | null | undefined) {
  const k = (key || 'unknown').trim() || 'unknown'
  let row = map.get(k)
  if (!row) {
    row = { key: k, label: k, critical: 0, warning: 0, ok: 0, total: 0 }
    map.set(k, row)
  }
  row.total += 1
  if (sev === 0) row.critical += 1
  else if (sev === 1) row.warning += 1
  else row.ok += 1
}

async function fetchStreamNodes(): Promise<StreamNode[]> {
  const res = await authFetch(`${API_BASE_URL}/api/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      view: 'stream',
      dimension: 'studio',
      time: { mode: 'live' },
      filters: {},
      slice: { type: 'global', value: '*' },
      limit: 5000,
      offset: 0,
    }),
  })
  if (!res.ok) throw new Error(`stream search: HTTP ${res.status}`)
  const body = await res.json() as { nodes?: StreamNode[] }
  return body.nodes ?? []
}

export default function ReportsView() {
  const [studioRows, setStudioRows] = useState<ReportRow[]>([])
  const [gameRows, setGameRows] = useState<ReportRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    void (async () => {
      try {
        const nodes = await fetchStreamNodes()
        if (cancelled) return
        const byStudio = new Map<string, ReportRow>()
        const byGame = new Map<string, ReportRow>()
        for (const n of nodes) {
          accumulate(byStudio, String(n.studio ?? 'unknown'), n.avgSeverity)
          accumulate(byGame, String(n.game ?? 'unknown'), n.avgSeverity)
        }
        setStudioRows([...byStudio.values()])
        setGameRows([...byGame.values()])
      } catch (e) {
        if (!cancelled) setError(String((e as Error)?.message ?? e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const totals = useMemo(() => {
    const sum = (rows: ReportRow[]) => rows.reduce(
      (acc, r) => ({
        critical: acc.critical + r.critical,
        warning: acc.warning + r.warning,
        total: acc.total + r.total,
      }),
      { critical: 0, warning: 0, total: 0 },
    )
    return { studio: sum(studioRows), game: sum(gameRows) }
  }, [studioRows, gameRows])

  const renderTable = (title: string, rows: ReportRow[]) => (
    <div className="panel" style={{ flex: 1, minWidth: 280, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div className="panel-head">
        <span className="panel-title">{title}</span>
        <span style={{ fontSize: 10, color: 'var(--t-2)', marginLeft: 'auto' }}>{rows.length}</span>
      </div>
      <div style={{ overflow: 'auto', flex: 1 }}>
        <table className="vms-table">
          <thead>
            <tr>
              <th>Code</th>
              <th style={{ textAlign: 'right' }}>Critical</th>
              <th style={{ textAlign: 'right' }}>Warning</th>
              <th style={{ textAlign: 'right' }}>OK</th>
              <th style={{ textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {[...rows].sort((a, b) => b.critical - a.critical || b.warning - a.warning).map((r) => (
              <tr key={r.key}>
                <td style={{ fontWeight: 500 }}>{r.label || r.key}</td>
                <td style={{ textAlign: 'right', color: r.critical ? 'var(--crit)' : 'var(--t-2)' }}>{r.critical}</td>
                <td style={{ textAlign: 'right', color: r.warning ? 'var(--warn)' : 'var(--t-2)' }}>{r.warning}</td>
                <td style={{ textAlign: 'right', color: 'var(--t-2)' }}>{r.ok}</td>
                <td style={{ textAlign: 'right' }}>{r.total}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', color: 'var(--t-2)', padding: 16 }}>
                  {loading ? 'Loading…' : 'No data'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
      <div style={{ fontSize: 11, color: 'var(--t-2)' }}>
        Current critical / warning stream counts by studio and game
        {error ? <span style={{ color: 'var(--crit)', marginLeft: 8 }}>{error}</span> : null}
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--t-2)' }}>
        <span>Studios · crit {totals.studio.critical} / warn {totals.studio.warning} / total {totals.studio.total}</span>
        <span>Games · crit {totals.game.critical} / warn {totals.game.warning} / total {totals.game.total}</span>
      </div>
      <div style={{ flex: 1, display: 'flex', gap: 10, minHeight: 0 }}>
        {renderTable('By studio', studioRows)}
        {renderTable('By game', gameRows)}
      </div>
    </div>
  )
}
